import { isEmpty, find, uniqBy } from 'lodash';

import { npsOptions } from 'containers/QuestionnairePage/constants';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_RATING,
  QUESTION_NPS,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_RANKING,
  QUESTION_DRILLDOWN,
  NODE_TYPES_SUPPORT_COLUMNS,
} from 'data/nodeTypes';
import {
  QuestionnaireConditionContext,
  ActionVisibilityValueType,
} from 'utils/adl-services';
import {
  findChildrenOnDepth,
  findDrillDownDepthFromConditions,
} from 'containers/QuestionnairePage/GenericQuestion/DrillDownQuestion/utils';
import {
  EMPTY_ANSWER_LABEL,
  EMPTY_ROW_LABEL,
  EMPTY_ROWS_LABEL,
  EMPTY_COLUMN_LABEL,
} from './constants';

export const getDemographicOptions = (taxonomy, targeting = {}) => {
  const foundTaxonomy = find(targeting.demographics, ['key', taxonomy]);

  const options = (foundTaxonomy || {}).values || [];
  return options.map((option) => {
    const { value, label } = option;

    return {
      key: value,
      value,
      label,
      allowSelection: true,
    };
  });
};

export function getLabel(label, valueType) {
  if (label.trim().length > 0) {
    return label;
  }

  switch (valueType) {
    case ActionVisibilityValueType.Row:
      return EMPTY_ROW_LABEL;
    case ActionVisibilityValueType.Column:
      return EMPTY_COLUMN_LABEL;
    default:
      return EMPTY_ANSWER_LABEL;
  }
}

export function isAnswerUnconditionallyHidden({
  nodes,
  answersWithLogicFromOutcomesHide,
  item,
}) {
  if (answersWithLogicFromOutcomesHide.includes(item.refId)) {
    return true;
  }

  if (item.isCarriedForward) {
    const sourceQuestion = find(
      nodes,
      (node) => node.refId === item.questionRefId,
    );

    let newItem;
    if (item.originalAnswerRefId) {
      newItem = find(
        sourceQuestion.answers,
        (answer) => answer.refId === item.originalAnswerRefId,
      );
    }

    if (!newItem && item.originalAnswerRefId) {
      newItem = find(
        sourceQuestion?.matrix?.matrixAnswers || [],
        (matrixAnswer) => matrixAnswer.refId === item.originalAnswerRefId,
      );
    }

    if (!newItem && item.originalScorepointRefId) {
      newItem = find(
        sourceQuestion.scorepoints,
        (scorepoint) => scorepoint.refId === item.originalScorepointRefId,
      );
    }

    if (!newItem && item.originalScorepointRefId) {
      newItem = find(
        sourceQuestion?.matrix?.columns || [],
        (matrixColumn) => matrixColumn.refId === item.originalScorepointRefId,
      );
    }

    if (newItem) {
      return isAnswerUnconditionallyHidden({
        nodes,
        answersWithLogicFromOutcomesHide,
        item: newItem,
      });
    }
  }

  return false;
}

const getAnswerOptions = (question, conditions) => {
  switch (question.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      return question.answers.map(
        ({ refId, body, media, allowSelection, isUnconditionallyHidden }) => ({
          key: refId,
          value: refId,
          label: getLabel(body),
          media,
          allowSelection,
          isUnconditionallyHidden,
        }),
      );
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      const rows = question.answers.map(
        ({ refId, body, allowSelection, isUnconditionallyHidden }) => ({
          key: refId,
          value: refId,
          label: getLabel(body, ActionVisibilityValueType.Row),
          allowSelection,
          isUnconditionallyHidden,
        }),
      );
      const columns = question.scorepoints.map(
        ({ refId, body, allowSelection, isUnconditionallyHidden }) => ({
          key: refId,
          value: refId,
          label: getLabel(body, ActionVisibilityValueType.Column),
          allowSelection,
          isUnconditionallyHidden,
        }),
      );

      return { rows, columns };
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const { matrix = {} } = question;
      const { matrixAnswers = [] } = matrix;

      const rows = question.answers.map(({ refId, allowSelection }) => {
        const matrixAnswer = find(matrixAnswers, ['answerRefId', refId]) || {};
        return {
          key: refId,
          value: refId,
          allowSelection,
          label:
            !matrixAnswer.beforeLabel && !matrixAnswer.afterLabel
              ? EMPTY_ROWS_LABEL
              : `${getLabel(
                  matrixAnswer.beforeLabel,
                  ActionVisibilityValueType.Row,
                )} | ${getLabel(
                  matrixAnswer.afterLabel,
                  ActionVisibilityValueType.Row,
                )}`,
        };
      });

      return { rows };
    }
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
    case QUESTION_MATRIX_OPEN_ENDED:
    case QUESTION_MATRIX_CONSTANT_SUM: {
      const { matrix = {} } = question;
      const { matrixAnswers = [], columns: matrixColumns = [] } = matrix;

      const rows = question.answers.map(({ refId, allowSelection }) => {
        const matrixAnswer = find(matrixAnswers, ['answerRefId', refId]) || {};
        return {
          key: refId,
          value: refId,
          allowSelection,
          label: getLabel(
            matrixAnswer.beforeLabel,
            ActionVisibilityValueType.Row,
          ),
        };
      });
      const columns = matrixColumns.map(
        ({ refId, beforeLabel, allowSelection }) => ({
          key: refId,
          value: refId,
          label: getLabel(beforeLabel, ActionVisibilityValueType.Column),
          allowSelection,
        }),
      );

      return { rows, columns };
    }
    // Ranking questions' columns are just numbers
    case QUESTION_RANKING: {
      const rows = question.answers.map(({ refId, body, allowSelection }) => ({
        key: refId,
        value: refId,
        label: body,
        allowSelection,
      }));
      const allowedCount = question.answers.filter(
        ({ allowSelection = true }) => allowSelection,
      ).length;

      const columns = question.answers.map((_, index) => ({
        key: `${index + 1}`,
        value: `${index + 1}`,
        label: `${index + 1}`,
        allowSelection: index < allowedCount,
      }));

      return { rows, columns };
    }
    case QUESTION_RATING: {
      const answers = new Array(question.ratingSize).fill(undefined);
      // we need to start from 1
      const values = answers.map((_, index) => ({
        key: index + 1,
        value: index + 1,
        label: `${index + 1} stars`,
        allowSelection: true,
      }));

      return values;
    }
    case QUESTION_NPS:
      return npsOptions.map(({ value, label }) => ({
        key: label,
        value,
        label,
        allowSelection: true,
      }));
    case QUESTION_DRILLDOWN: {
      const depth = findDrillDownDepthFromConditions(question, conditions);
      const children = findChildrenOnDepth(
        {
          children:
            question.matrix.validation.drillDownConfigurationDto.config.levels,
        },
        depth,
      );

      return uniqBy(children, 'value').map(({ refId, value }) => ({
        key: refId,
        value,
        label: value,
        allowSelection: true,
      }));
    }
    default:
      return [];
  }
};

export const getConceptOptions = (
  abTestNode,
  fallbackLabelGetter = () => ``,
) => {
  if (!abTestNode || !abTestNode.concepts) {
    return [];
  }
  return abTestNode.concepts.map((concept, index) => ({
    key: concept.refId,
    value: concept.refId,
    label: concept.name || fallbackLabelGetter(index + 1),
    allowSelection: true,
  }));
};

export const getLoopAndMergeOptions = (
  loopAndMergeNode,
  fallbackLabelGetter = () => ``,
) => {
  if (!loopAndMergeNode || !loopAndMergeNode.concepts) {
    return [];
  }
  return loopAndMergeNode.concepts.map((concept, index) => ({
    key: concept.refId,
    value: concept.refId,
    label: concept.name || fallbackLabelGetter(index + 1),
    allowSelection: true,
  }));
};

export const getDerivedContextFromConditions = (conditions = []) => {
  if (isEmpty(conditions)) {
    return undefined;
  }
  const { context } = conditions[0];
  const hasEveryConditionTheSameContext = conditions.every(
    (condition) =>
      !condition.context || condition.context.questionId === context.questionId,
  );

  if (!hasEveryConditionTheSameContext) {
    throw new Error(
      'Unexpected Context: Conditions under the same rule contain different contexts',
    );
  }

  return context;
};

export const areAllLeafs = (conditions) =>
  Boolean(conditions.every((condition) => !condition.operator));

export function getDefaultVariableValueFromContext(context, hiddenVariables) {
  const referencedVariable = hiddenVariables.find(
    (variable) => variable.refId === context?.variableId,
  );

  return referencedVariable?.values[0]?.value;
}

export function getVariableValuesFromContext(context, hiddenVariables) {
  const referencedVariable = hiddenVariables.find(
    (variable) => variable.refId === context.variableId,
  );

  return referencedVariable.values.map((value) => ({
    ...value,
    value: value.refId,
    allowSelection: true,
  }));
}

export const getAnswerOptionsByContextType = (
  node,
  conditions,
  contextType,
) => {
  const answersOptions = getAnswerOptions(node, conditions);

  // Visibility value select lists only answers/rows/columns.
  // Question-level visibility uses dedicated matchers (no value select).
  if (contextType === QuestionnaireConditionContext.Visibility) {
    if (
      NODE_TYPES_SUPPORT_COLUMNS.includes(node?.type) ||
      answersOptions?.rows
    ) {
      return [
        ...(answersOptions.rows || []),
        ...(answersOptions.columns || []),
      ];
    }

    return Array.isArray(answersOptions) ? answersOptions : [];
  }

  return answersOptions;
};

export function enrichQuestionByAnswersWithLogicFromOutcomesHide({
  nodes,
  question,
  answersWithLogicFromOutcomesHide,
}) {
  // Hidden answers can't be selected; rules kept on them show them in red.
  const withHideWarning = (item) => {
    const isHidden = isAnswerUnconditionallyHidden({
      nodes,
      answersWithLogicFromOutcomesHide,
      item,
    });
    return {
      ...item,
      allowSelection: !isHidden,
      isUnconditionallyHidden: isHidden,
    };
  };

  const node = { ...question };

  if (question?.answers) {
    node.answers = node.answers.map(withHideWarning);
  }

  if (question?.matrix?.matrixAnswers) {
    node.matrix = {
      ...node.matrix,
      matrixAnswers: node.matrix.matrixAnswers.map(withHideWarning),
    };
  }

  if (question?.scorepoints) {
    node.scorepoints = node.scorepoints.map(withHideWarning);
  }

  if (question?.matrix?.columns) {
    node.matrix = {
      ...node.matrix,
      columns: node.matrix.columns.map(withHideWarning),
    };
  }

  return node;
}
